import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import { FileBarChart, Wallet, Package, Store, TrendingUp, Download, X } from "lucide-react";

function jakartaMonth() {
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", timeZone: "Asia/Jakarta" })
    .format(new Date()).slice(0, 7);
}

export default function Reports() {
  const [month, setMonth] = useState(jakartaMonth());
  const [outletId, setOutletId] = useState("all");
  const { data: outlets = [] } = useQuery({ queryKey: ["outlets"], queryFn: async () => (await api.get("/outlets")).data });
  const outletParam = outletId !== "all" ? `&outlet_id=${outletId}` : "";
  const { data, isLoading } = useQuery({
    queryKey: ["monthly", month, outletId],
    queryFn: async () => (await api.get(`/reports/monthly?month=${month}${outletParam}`)).data,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const exportPdf = async () => {
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
    try {
      const res = await api.get(`/reports/monthly/pdf?month=${month}${outletParam}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (isMobile) {
        setPreviewUrl(url); // buka pratinjau ramah layar kecil di HP
      } else {
        const a = document.createElement("a");
        a.href = url; a.download = `rekap_${month}.pdf`; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.success("PDF rekap diunduh.");
      }
    } catch { toast.error("Gagal ekspor PDF."); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };

  return (
    <div className="space-y-6 nh-fade">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><FileBarChart className="w-6 h-6" /> Rekap Bulanan</h1>
          <p className="text-sm text-muted-foreground">Ringkasan belanja disetujui per kategori dan per outlet.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground">Outlet</label>
            <Select value={outletId} onValueChange={setOutletId}>
              <SelectTrigger className="mt-1 h-9 w-48" data-testid="reports-outlet"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Outlet</SelectItem>
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Bulan</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="reports-month" className="mt-1 h-9 w-44" />
          </div>
          <Button variant="outline" onClick={exportPdf} data-testid="reports-export-pdf" className="h-9 rounded-full">
            <Download className="w-4 h-4 mr-2" /> Ekspor PDF
          </Button>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-4">{[0,1,2,3].map(i => <div key={i} className="h-24 rounded-xl bg-secondary animate-pulse" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="p-5" data-testid="report-total">
              <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Total Belanja</span><Wallet className="w-4 h-4" /></div>
              <p className="mt-3 text-2xl font-bold font-mono">{formatRupiah(data.total)}</p>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Baris/Item</span><Package className="w-4 h-4" /></div>
              <p className="mt-3 text-2xl font-bold font-mono">{data.entry_count}</p>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Kategori Teratas</span><TrendingUp className="w-4 h-4" /></div>
              <p className="mt-3 text-lg font-bold truncate">{data.by_category[0]?.category || "—"}</p>
              <p className="text-xs text-muted-foreground font-mono">{formatRupiah(data.by_category[0]?.total || 0)}</p>
            </Card>
            <Card className="p-5">
              <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Outlet Terbesar</span><Store className="w-4 h-4" /></div>
              <p className="mt-3 text-lg font-bold truncate">{data.by_outlet[0]?.outlet || "—"}</p>
              <p className="text-xs text-muted-foreground font-mono">{formatRupiah(data.by_outlet[0]?.total || 0)}</p>
            </Card>
          </div>

          {data.entry_count === 0 ? (
            <Card className="p-10 text-center text-muted-foreground text-sm">Belum ada belanja disetujui pada bulan ini.</Card>
          ) : (
            <>
              <div className="grid gap-6 lg:grid-cols-2">
                <Card className="p-5">
                  <h2 className="font-semibold mb-4">Belanja per Kategori</h2>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.by_category} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => "Rp " + (v/1000) + "k"} />
                      <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={90} stroke="hsl(var(--muted-foreground))" />
                      <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="total" fill="hsl(var(--primary))" radius={[0, 6, 6, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card className="p-5">
                  <h2 className="font-semibold mb-4">Belanja per Outlet</h2>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.by_outlet}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis dataKey="outlet" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => "Rp " + (v/1000) + "k"} />
                      <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="total" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={64} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              <Card className="p-5">
                <h2 className="font-semibold mb-4">Tren Belanja Harian</h2>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={data.by_day}>
                    <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} /><stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => d?.slice(8)} />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => "Rp " + (v/1000) + "k"} />
                    <Tooltip formatter={(v) => formatRupiah(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" fill="url(#g)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <Card className="p-5">
                <h2 className="font-semibold mb-3">10 Barang Terbesar</h2>
                <div className="divide-y divide-border/60">
                  {data.top_items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between py-2">
                      <span className="text-sm"><span className="text-muted-foreground font-mono mr-2">{i + 1}.</span>{it.item}</span>
                      <span className="font-mono text-sm">{formatRupiah(it.total)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </>
      )}
      {previewUrl && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col" data-testid="pdf-preview">
          <div className="flex items-center justify-between px-4 h-12 text-white">
            <span className="text-sm font-medium">Pratinjau PDF Rekap</span>
            <button onClick={closePreview} data-testid="pdf-preview-close" className="p-1"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 bg-white overflow-hidden">
            <iframe title="Pratinjau PDF Rekap" src={previewUrl} className="w-full h-full border-0" />
          </div>
          <div className="p-3 bg-black flex gap-2" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <a href={previewUrl} target="_blank" rel="noreferrer" data-testid="pdf-open-tab"
              className="flex-1 text-center py-2.5 rounded-full bg-white/15 text-white text-sm font-medium">Buka di Tab Baru</a>
            <a href={previewUrl} download={`rekap_${month}.pdf`} data-testid="pdf-download"
              className="flex-1 text-center py-2.5 rounded-full bg-white text-black text-sm font-medium">Unduh PDF</a>
          </div>
        </div>
      )}
    </div>
  );
}
